/**
 * @file WholeSitePanel.tsx
 * @description Содержимое раздела «Весь сайт» в конструкторе (phase19.16
 * Р1-6а; uiux-builder-shell.md §2.1 таблица): готовые панели Р1 —
 * оформление (с вкладкой «Дизайны»), дизайны, меню, настройки сайта, SEO
 * (с Р3в — ссылка на раздел «Сайт → SEO», SB145),
 * лента блога, вид корзины (SB120 этап 4), счётчики и cookie (Р3г),
 * история публикаций — в своей
 * узкой раскладке. Вкладка или раздел — из адреса. Без права на часть —
 * объяснение, кто её меняет (дизайны и вид корзины — только просмотр).
 */

import type { SiteViewPart } from "@chaiberi/shared";
import { Lock } from "lucide-react";
import { useSearchParams } from "react-router";

import { EmptyState } from "../../ui/EmptyState";
import { AppearancePanel, type AppearanceTab } from "../../site-view/appearance/AppearancePanel";
import { BlogPanel } from "../../site-view/blog/BlogPanel";
import { CartLookPanel } from "../../site-view/cart/CartLookPanel";
import { CatalogPanel } from "../../site-view/catalog/CatalogPanel";
import { CountersPanel } from "../../site-view/counters/CountersPanel";
import { SiteHistoryPanel } from "../../site-view/history/SiteHistoryPanel";
import { MenuEditor, type MenuEditorTab } from "../../site-view/menu/MenuEditor";
import { SeoMovedNotice } from "../../site-seo/SeoMovedNotice";
import { SiteSettingsPanel } from "../../site-view/settings/SiteSettingsPanel";
import { useSiteView } from "../../site-view/site-view-context";
import { useBuilder } from "../builder-context";
import { DesignsPanel } from "../designs/DesignsPanel";
import { useCurrentContext } from "../../../auth/hooks";
import { BUILDER_PANEL_META } from "../../../lib/site-builder/panels";
import { builderLink, type BuilderPanelKey } from "../../../lib/site-builder/selection";
import { fontsTabCarry } from "../../../lib/site-view/fonts-tab-params";
import { asSettingsSection } from "../../../lib/site-view/settings-sections";
import type { SiteViewDraft } from "../../../lib/site-view/types";

type WholeSiteKey = Exclude<BuilderPanelKey, "pages">;

// v0.19.65 (SB146): `crumbs` — вкладка «Крошки»; ключ должен быть и в `PANEL_SUBS.menu` (selection.ts).
const MENU_TABS: Readonly<Record<string, MenuEditorTab>> = { header: "HEADER", footer: "FOOTER", phone: "PHONE", crumbs: "CRUMBS" };

interface Props {
  panel: WholeSiteKey;
  draft: SiteViewDraft;
  /** Панель раскрыта на место канвы (широкий раздел на компьютере). */
  spread: boolean;
}

export function WholeSitePanel({ panel, draft, spread }: Props) {
  const b = useBuilder();
  const view = useSiteView();
  const slug = useCurrentContext()?.shop?.slug ?? "";
  const [params] = useSearchParams();
  const { selection } = b;
  const sub = selection.sub;
  const part: SiteViewPart | null = BUILDER_PANEL_META[panel].part;
  // Без права — объяснение; панели «только для просмотра» (дизайны, вид корзины) показываются неактивными.
  if (part && !view.editableParts.includes(part) && !BUILDER_PANEL_META[panel].readable) {
    return <EmptyState variant="forbidden" icon={Lock} title="Только просмотр" description={BUILDER_PANEL_META[panel].forbidden} />;
  }
  const setSub = (next: string | null) => void b.select({ sub: next });

  switch (panel) {
    case "appearance":
      return (
        <AppearancePanel
          draft={draft}
          tab={(sub as AppearanceTab | null) ?? "colors"}
          // Возврат к элементу («← К элементу») переживает смену вкладки.
          onTab={(tab) => void b.select({ sub: tab }, fontsTabCarry(params))}
          designs={<DesignsPanel />}
        />
      );
    case "designs":
      return <DesignsPanel />;
    case "menu":
      return (
        <MenuEditor
          draft={draft}
          narrow={!spread}
          tab={MENU_TABS[sub ?? "header"] ?? "HEADER"}
          onTab={(tab) => setSub(tab.toLowerCase())}
        />
      );
    case "settings":
      return <SiteSettingsPanel section={asSettingsSection(sub)} onSection={setSub} narrow={!spread} />;
    case "seo":
      // Р3в (SB145): SEO-настройки сайта — отдельный раздел, здесь только ссылка.
      return <SeoMovedNotice shopSlug={slug} />;
    case "counters":
      // Вкладка — что показывает канва: баннер или окно «Настроить».
      return <CountersPanel canvasView={sub} onCanvasView={setSub} />;
    case "blog":
      return <BlogPanel draft={draft} />;
    case "catalog":
      // Вкладка из адреса (`panel=catalog.card` — ссылка из элемента «Товары»).
      return <CatalogPanel key={sub ?? "list"} initialTab={sub === "card" ? "card" : "list"} />;
    case "cart":
      return <CartLookPanel />;
    case "history":
      return (
        <SiteHistoryPanel
          openedVersion={selection.version}
          onOpen={(version) => void b.select({ version })}
          rowHref={(version) => builderLink(slug, { ...selection, version })}
        />
      );
  }
}
